import { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Box,
  alpha,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import tenant from '@/services/TenantService';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';

const POLLING_INTERVAL = 5000; // 5 seconds

const getStatusText = (status) => {
  switch (status) {
    case 'NEW':
      return 'Neu';
    case 'CONFIGURED_TOKEN':
      return 'Token konfiguriert';
    case 'OK':
      return 'Erfolgreich abgeschlossen';
    default:
      return 'Status wird abgerufen...';
  }
};

const getStatusSeverity = (status) => {
  if (status === 'OK') return 'success';
  return 'info';
};

const DeploymentStatus = ({
  deployment,
  resetToFirstStep,
  handleCancel,
  handleFinishDeploymentSetup,
}) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tenant.get(
        `/deployment/${deployment.insertedId || deployment._id}`,
      );
      const newStatus = response.data?.config?.status;
      const newError = response.data?.config?.error;

      setStatus(newStatus);
      setError(newError);

      if (newError) {
        toast.error(
          <ToastMessage title="Bei der Konfiguration ist ein Fehler aufgetreten." />,
        );
      } else if (newStatus === 'OK') {
        toast.success(
          <ToastMessage title="Die Konfiguration wurde erfolgreich abgeschlossen." />,
        );
        await tenant.put(`/deployment/${response?.data?._id}`, {
          ...response.data,
          config: {
            ...response.data.config,
            isMaintenanceActive: true,
            status: 'TOKEN_CONFIGURED',
          },
        });
        // hier zurück auf hauptseite
        handleFinishDeploymentSetup(deployment.insertedId || deployment._id);
      }
      // eslint-disable-next-line no-unused-vars
    } catch (apiError) {
      toast.error(<ToastMessage title="Fehler beim Abrufen des Status." />);
      setError({
        message: 'Die Verbindung zum Server ist fehlgeschlagen.',
      });
    } finally {
      setLoading(false);
    }
  }, [deployment.insertedId, deployment._id, handleFinishDeploymentSetup]);

  useEffect(() => {
    fetchStatus();
    const intervalId = setInterval(fetchStatus, POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchStatus]);

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<CheckCircleIcon color="primary" />}
        title={<Typography variant="h6">Deployment Status</Typography>}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2, minHeight: 200, flex: 1 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Der Konfigurationsstatus wird automatisch überprüft. Dieser Vorgang
          kann einige Minuten dauern.
        </Alert>

        {status && !error && (
          <Alert
            severity={getStatusSeverity(status)}
            sx={{
              '& .MuiAlert-message': {
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              },
            }}
          >
            {loading && <CircularProgress size={16} />}
            <span>Status: {getStatusText(status)}</span>
          </Alert>
        )}

        {error && (
          <Alert severity="error">
            <strong>Fehler bei der Konfiguration:</strong>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
              {error.message || 'Unbekannter Fehler aufgetreten.'}
            </Box>
          </Alert>
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={resetToFirstStep} variant="outlined">
          Zurück
        </Button>
        {handleCancel && (
          <Button onClick={handleCancel} color="inherit">
            Abbrechen
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default DeploymentStatus;
